'use client';

import {
  ArrowLeft,
  Captions,
  Film,
  Layers3,
  MessageSquare,
  Music,
  SlidersHorizontal,
  Sparkles,
  Sticker,
  Type,
  Wand2,
} from 'lucide-react';

export type EditorLeftMode =
  | 'chat'
  | 'media'
  | 'text'
  | 'stickers'
  | 'effects'
  | 'audio'
  | 'transitions'
  | 'captions'
  | 'filters'
  | 'templates';

// chat first, then the same tools in the same order as the top-bar tabs.
const ITEMS: Array<{ mode: EditorLeftMode; icon: typeof MessageSquare; label: string }> = [
  { mode: 'chat', icon: MessageSquare, label: 'ИИ-монтажёр' },
  { mode: 'media', icon: Film, label: 'Медиа' },
  { mode: 'text', icon: Type, label: 'Текст' },
  { mode: 'stickers', icon: Sticker, label: 'Стикеры' },
  { mode: 'effects', icon: Sparkles, label: 'Эффекты' },
  { mode: 'audio', icon: Music, label: 'Аудио' },
  { mode: 'transitions', icon: Wand2, label: 'Переходы' },
  { mode: 'captions', icon: Captions, label: 'Субтитры' },
  { mode: 'filters', icon: SlidersHorizontal, label: 'Фильтры' },
  { mode: 'templates', icon: Layers3, label: 'Шаблоны' },
];

/**
 * Full-height left rail: back arrow at the very top, then the nav items.
 * Spans the entire editor height (the top bar sits to its right).
 */
export function EditorIconRail({
  active,
  onSelect,
  onExitToHub,
}: {
  active: EditorLeftMode;
  onSelect: (mode: EditorLeftMode) => void;
  onExitToHub: () => void;
}) {
  return (
    <nav data-lenis-prevent aria-label="Инструменты редактора" className="flex h-full w-[76px] shrink-0 flex-col items-center gap-1 overflow-y-auto border-r border-white/10 bg-[#0b0b0c] py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <button
        type="button"
        onClick={onExitToHub}
        title="К проектам"
        aria-label="К проектам"
        className="flex h-10 w-14 items-center justify-center rounded-lg text-white/60 transition hover:bg-white/10 hover:text-white"
      >
        <ArrowLeft className="h-5 w-5" strokeWidth={1.8} />
      </button>
      <div className="my-1 h-px w-10 bg-white/10" />

      {ITEMS.map((it) => {
        const Icon = it.icon;
        const on = active === it.mode;
        return (
          <button
            key={it.mode}
            type="button"
            onClick={() => onSelect(it.mode)}
            title={it.label}
            aria-label={it.label}
            aria-pressed={on}
            className={`flex h-[52px] w-[68px] shrink-0 flex-col items-center justify-center gap-1 rounded-lg px-1 transition ${
              on
                ? 'bg-brand-600/20 text-brand-200 ring-1 ring-brand-500/40'
                : 'text-white/45 hover:bg-white/5 hover:text-white/80'
            }`}
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
            <span className="max-w-full truncate text-[9px] font-medium leading-none">{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
